import { ImgHTMLAttributes } from 'react';

interface MentaraIconProps extends ImgHTMLAttributes<HTMLImageElement> {
  className?: string;
}

/**
 * Mentara manta ray icon — uses the actual brand logo PNGs.
 * Automatically switches between light (pink) and dark (burgundy) variants
 * based on the current theme.
 * 
 * File naming: "dark" = burgundy colored, "light" = pink colored.
 * Light mode → burgundy icon (dark.png), Dark mode → pink icon (light.png).
 * 
 * The PNGs have whitespace padding, so we use overflow-hidden + scale
 * to crop in on the ray without increasing the element's footprint.
 */
export function MantaRayIcon({ className, ...props }: MentaraIconProps) {
  // The PNG has transparent padding around the ray. We use negative margins
  // to pull the visual content closer, making the ray appear larger without clipping.
  return (
    <>
      <img
        src="/logos/mentara-icon-light.png"
        alt="Mentara"
        className={`dark:hidden object-contain h-[40px] w-[40px] ${className ?? ''}`}
        width={40}
        height={40}
        decoding="async"
        {...props}
      />
      <img
        src="/logos/mentara-icon-dark.png"
        alt="Mentara"
        className={`hidden dark:block object-contain h-[40px] w-[40px] ${className ?? ''}`}
        width={40}
        height={40}
        decoding="async"
        {...props}
      />
    </>
  );
}

/**
 * Full Mentara logo (manta ray + wordmark) — for larger branding areas.
 */
export function MentaraFullLogo({ className, ...props }: MentaraIconProps) {
  return (
    <>
      <img
        src="/logos/mentara-full-dark.png"
        alt="Mentara"
        className={`dark:hidden ${className ?? ''}`}
        {...props}
      />
      <img
        src="/logos/mentara-full-light.png"
        alt="Mentara"
        className={`hidden dark:block ${className ?? ''}`}
        {...props}
      />
    </>
  );
}

/**
 * Mentara wordmark only (no icon) — for tight spaces.
 */
export function MentaraWordmark({ className, ...props }: MentaraIconProps) {
  return (
    <>
      <img
        src="/logos/mentara-wordmark-dark.png"
        alt="Mentara"
        className={`dark:hidden ${className ?? ''}`}
        {...props}
      />
      <img
        src="/logos/mentara-wordmark-light.png"
        alt="Mentara"
        className={`hidden dark:block ${className ?? ''}`}
        {...props}
      />
    </>
  );
}
